import {Card} from "@/components/card/Card.tsx";
import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {PasswordField} from "@/components/form/fields/PasswordField/PasswordField.tsx";
import {SelectField} from "@/components/form/fields/SelectField/SelectField.tsx";
import {SwitchField} from "@/components/form/fields/SwitchField.tsx";
import {TagsField} from "@/components/form/fields/TagsField/TagsField.tsx";
import {TextAreaField} from "@/components/form/fields/TextAreaField.tsx";
import {ServiceField} from "@/lib/constants/service.ts";
import {
  MonitoringServiceChoices,
  MonitoringServiceType,
  ServiceFieldDefinition,
  ServiceTag,
  ServiceVersionDefinition,
} from "@/lib/types/checks.ts";
import {FieldType} from "@/pages/checks/components/form/fields/common.ts";
import {DNSAutoloadField} from "@/pages/checks/components/form/fields/legacy/DNSAutoloadField.tsx";
import {GroupChecksSelector} from "@/pages/checks/components/form/fields/legacy/GroupChecksSelector.tsx";
import {WhoIsAutoloadField} from "@/pages/checks/components/form/fields/legacy/WhoIsAutoloadField.tsx";
import {RadioGroupField} from "@/pages/checks/components/form/fields/RadioGroupField.tsx";
import {RDAPLookupField} from "@/pages/checks/components/form/fields/RDAPLookupField.tsx";
import {ServiceSelectField} from "@/pages/checks/components/form/fields/ServiceSelectField.tsx";
import {VersionField} from "@/pages/checks/components/form/fields/VersionField.tsx";
import {FIELD_PLACEHOLDERS} from "@/pages/checks/constants.ts";

import {PercentageField} from "./PercentageField.tsx";

const IGNORE_REQUIRED_ATTRIBUTE: Record<string, string[]> = {
  [MonitoringServiceType.GROUP]: [ServiceField.GROUP_CHECK_TAGS, ServiceField.GROUP_CHECK_DOWN_CONDITION],
};

interface CheckFormAutoGeneratedFieldProps {
  type: FieldType;
  mspChoices: MonitoringServiceChoices;
  field: ServiceFieldDefinition;
  serviceType: MonitoringServiceType;
  versions: ServiceVersionDefinition[];
  serviceTags?: ServiceTag[];
  toggleSubform?: () => void;
}

export const CheckFormAutoGeneratedField = ({
  type,
  serviceTags,
  toggleSubform,
  serviceType,
  field: {field: name, verbose_name, title, optional, support_link, help_text},
  mspChoices,
  versions,
}: CheckFormAutoGeneratedFieldProps) => {
  const forceNonRequired = IGNORE_REQUIRED_ATTRIBUTE[serviceType] || [];
  const required = !optional && !forceNonRequired.includes(name);
  const placeholder = FIELD_PLACEHOLDERS[name as ServiceField];

  if (type === FieldType.Text) {
    return (
      <InputField
        name={name}
        label={verbose_name}
        tooltip={title}
        supportLink={support_link}
        helpText={help_text}
        required={required}
        placeholder={placeholder}
      />
    );
  }
  if (type === FieldType.Percentage) {
    return (
      <PercentageField
        name={name}
        label={verbose_name}
        tooltip={title}
        supportLink={support_link}
        helpText={help_text}
        required={required}
      />
    );
  }
  if (type === FieldType.Password) {
    return (
      <PasswordField
        name={name}
        label={verbose_name}
        tooltip={title}
        supportLink={support_link}
        helpText={help_text}
        required={required}
      />
    );
  }
  if (type === FieldType.CheckBox) {
    return (
      <Card variant={"white"}>
        <SwitchField
          name={name}
          label={verbose_name}
          description={title}
          supportLink={support_link}
          helpText={help_text}
          required={required}
          labelPosition={"left"}
        />
      </Card>
    );
  }
  if (type === FieldType.Select) {
    const options = mspChoices[name];
    return (
      <SelectField
        name={name}
        label={verbose_name}
        options={options}
        tooltip={title}
        supportLink={support_link}
        helpText={help_text}
        required={required}
      />
    );
  }
  if (type === FieldType.Version) {
    return (
      <VersionField
        name={name}
        label={verbose_name}
        options={versions}
        tooltip={title}
        supportLink={support_link}
        helpText={help_text}
      />
    );
  }
  if (type === FieldType.TextArea) {
    return (
      <TextAreaField
        name={name}
        label={verbose_name}
        tooltip={title}
        supportLink={support_link}
        helpText={help_text}
        required={required}
        placeholder={placeholder}
      />
    );
  }
  if (type === FieldType.DNSAutoload) {
    return (
      <DNSAutoloadField
        name={name}
        label={verbose_name}
        tooltip={title}
        supportLink={support_link}
        helpText={help_text}
        required={required}
      />
    );
  }
  if (type === FieldType.RDAPAutoload) {
    return (
      <RDAPLookupField
        name={name}
        label={verbose_name}
        tooltip={title}
        supportLink={support_link}
        helpText={help_text}
        required={required}
      />
    );
  }
  if (type === FieldType.WHOISAutoload) {
    return (
      <WhoIsAutoloadField
        name={name}
        label={verbose_name}
        tooltip={title}
        supportLink={support_link}
        helpText={help_text}
        required={required}
      />
    );
  }
  if (type === FieldType.Radio) {
    const options = mspChoices[name];
    return (
      <RadioGroupField
        name={name}
        label={verbose_name}
        tooltip={title}
        supportLink={support_link}
        helpText={help_text}
        options={options}
        required={required}
      />
    );
  }
  if (type === FieldType.Tags && serviceTags) {
    return (
      <TagsField
        name={name}
        label={verbose_name}
        tooltip={title}
        supportLink={support_link}
        helpText={help_text}
        tags={serviceTags}
        required={required}
      />
    );
  }
  if (type === FieldType.MultiCheckSelector && toggleSubform) {
    return (
      <GroupChecksSelector
        name={name}
        label="Manually select Checks"
        tooltip="Select the Checks you would like to include in this group"
        toggleSubform={toggleSubform}
      />
    );
  }
  if (type === FieldType.SingleCheckSelector) {
    return (
      <ServiceSelectField
        name={name}
        label={verbose_name}
        tooltip={title}
        supportLink={support_link}
        helpText={help_text}
        required={required}
      />
    );
  }

  console.warn(`field ${name} couldn't been found in ${serviceType}`);
  return null;
};
